import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useMemoizedFn } from 'ahooks';
import { Alert, Modal, ModalProps, Space, Table, Tag } from 'antd';
import CheckContainer from 'components/CheckContainer';
import Empty from 'components/Empty';
import FlexStyled from 'components/styled/flex';
import { uniq, without } from 'lodash-es';
import { memo, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useNoReceptionStoreOfDept, useReceptionRolesOfDept } from '../../hooks/data';
import useFilterInfo from './useFilterInfo';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface AssignReceptionModalProps extends Omit<ModalProps, 'onOk' | 'okButtonProps'> {
  departmentId: StoreHomeV2.Department['id'];
  onFinish?: (values: {
    roleIds: StoreHomeV2.RoleWithDepartment['id'][];
    stores: StoreHomeV2.NoReceptionStore[];
  }) => void;
}

const AssignReceptionModal = (props: AssignReceptionModalProps) => {
  const { departmentId, onFinish, ...ohterProps } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<StoreHomeV2.RoleWithDepartment['id'][]>(
    [],
  );
  const { data: stores } = useNoReceptionStoreOfDept(departmentId);
  const { data: roles } = useReceptionRolesOfDept(departmentId);
  const availableRoles = useMemo(() => {
    return roles ? roles.filter((r) => r.accounts.length > 0) : [];
  }, [roles]);
  const roleList = useMemo(() => {
    if (availableRoles.length < 1) {
      return (
        <Empty
          className={css`
            margin-top: 45%;
          `}
          description={t('department.部门暂无参与咨询分配的角色/角色下无帐号')}
        />
      );
    }
    return (
      <div>
        {availableRoles.map((role) => {
          return (
            <RoleCheckBox
              key={role.id}
              onChange={(checked) => {
                setSelectedRoleIds((ids) => {
                  if (checked) {
                    return uniq(ids.concat(role.id));
                  }
                  return without(ids, role.id);
                });
              }}
            >
              <Space direction="vertical" size={4}>
                <RoleName>{role.name}</RoleName>
                <Tag color="blue">{t('department.x帐号', { x: role.accounts?.length ?? 0 })}</Tag>
              </Space>
            </RoleCheckBox>
          );
        })}
      </div>
    );
  }, [availableRoles, t]);
  const ready = !!(selectedStoreIds.length && selectedRoleIds.length);
  const { platforms, regions } = useFilterInfo(stores);

  return (
    <StyledModal
      width={800}
      onOk={useMemoizedFn(() => {
        if (!stores) {
          return;
        }
        onFinish?.({
          roleIds: selectedRoleIds,
          stores: stores.filter((store) => {
            return selectedStoreIds.includes(store.id);
          }),
        });
      })}
      okButtonProps={{ disabled: !ready }}
      {...ohterProps}
    >
      {stores ? (
        <>
          <Alert
            type="warning"
            showIcon
            message={t('department.以下店铺未配置参与咨询分配的角色，买家会话将无帐号接待')}
          />
          <Content>
            <div>
              <SectionTitle>{t('department.店铺(x)', { x: stores.length })}</SectionTitle>
              <StoresTable
                pagination={{
                  defaultPageSize: 10,
                }}
                rowKey="id"
                sticky
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedStoreIds,
                  onChange: (selectedRowKeys) => {
                    setSelectedStoreIds(selectedRowKeys as number[]);
                  },
                }}
                dataSource={stores}
                columns={[
                  {
                    key: 'platform',
                    dataIndex: 'externalType',
                    title: t('market.平台'),
                    render: (text: string) => <>{text}</>,
                    filters: platforms.map((platform) => ({
                      text: platform,
                      value: platform,
                    })),
                    onFilter: (value, record: any) => {
                      return record.externalType === value;
                    },
                  },
                  {
                    key: 'region',
                    dataIndex: 'region',
                    title: t('phrase.站点'),
                    filters: regions.map((region) => ({
                      text: getRegionLabel(region as General.Region),
                      value: region,
                    })),
                    render: (text: string) => <>{getRegionLabel(text as General.Region)}</>,
                    onFilter: (value, record: any) => {
                      return record.region === value;
                    },
                  },
                  {
                    title: t('ViewShops.店铺名称'),
                    key: 'storeName',
                    dataIndex: 'storeName',
                  },
                ]}
              />
            </div>
            <RoleList>
              <SectionTitle>
                {t('department.选择角色(x)', { x: availableRoles.length })}
              </SectionTitle>
              {roleList}
            </RoleList>
          </Content>
        </>
      ) : null}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
`;
const Content = styled(FlexStyled.Flex)`
  padding: 20px;
  height: 75vh;
  gap: 20px;
  > div {
    flex-grow: 0;
    height: 100%;
    padding: 0 20px;
    border: 1px solid var(--fett-border-default);
    box-sizing: border-box;
    :last-of-type {
      flex: 0 0 276px;
    }
  }
`;
const SectionTitle = styled.h5`
  position: sticky;
  top: 0;
  left: 0;
  padding-top: 10px;
  background-color: var(--fett-bg-panel);
  font-size: 16px;
  line-height: 2;
  z-index: 2;
`;
const StoresTable = styled(Table)`
  .ant-table-body {
    height: 50vh;
    overflow: auto;
  }
`;

const RoleCheckBox = styled(CheckContainer)`
  width: 100%;
  display: block;
  margin-bottom: 12px;
`;
const RoleName = styled.p`
  margin: 0;
  padding: 0;
`;
const RoleList = styled.div`
  max-height: 100%;
  overflow: auto;
`;
export default memo(AssignReceptionModal);
